import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Grid3x3,
  List,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Star,
  Calendar,
  DollarSign,
  Clock,
  Users,
  Award,
  Building2,
  User,
  TrendingUp,
  Settings2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PaginationControls } from '@/components/pagination-controls';
import { usePagination } from '@/hooks/use-pagination';
import { paginateArray, type PaginationMetadata } from '@/types/pagination';
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import type { Employee as ApiEmployee, ResourceAllocation } from '@/types';
import { fetchEmployees, fetchResourceAllocations } from '@/lib/data-service';

interface Skill {
  name: string;
  proficiency: number; // 1-5
  category: string;
}

interface ProjectAllocation {
  projectId: number;
  projectName: string;
  projectCode: string;
  allocation: number; // percentage
  startDate: string;
  endDate: string;
  role: string;
}

interface WorkingPattern {
  name: string;
  fte: number;
  hoursPerDay: number;
  totalHoursPerWeek: number;
  workingDays: string[];
  /** Per-day hours keyed by full day name (e.g. { Monday: 7.5, Tuesday: 7.5 }). When present, takes precedence over the uniform hoursPerDay value. */
  dailyHours?: Record<string, number>;
}

interface Rates {
  billRate: {
    day: number;
    hour: number;
  };
  costRate: {
    day: number;
    hour: number;
  };
}

interface DirectoryResource {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  location: string;
  image: string;
  type: 'Person' | 'Asset';
  status: 'Active' | 'On Leave' | 'Inactive';
  grade: string;
  resourceType: string;
  skills: Skill[];
  workingPattern: WorkingPattern;
  projectAllocations: ProjectAllocation[];
  rates: Rates;
}

const DEFAULT_WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_HOURS_PER_WEEK = 40;

/** Map day-number strings ("1"–"7") to full day names. */
const DAY_NUMBER_MAP: Record<string, string> = {
  '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday',
  '5': 'Friday', '6': 'Saturday', '7': 'Sunday',
};

/** Map short and full day-name strings to canonical full names. */
const DAY_NAME_MAP: Record<string, string> = {
  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
  Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
  Monday: 'Monday', Tuesday: 'Tuesday', Wednesday: 'Wednesday',
  Thursday: 'Thursday', Friday: 'Friday', Saturday: 'Saturday', Sunday: 'Sunday',
};

function parseWorkingDays(workingDays?: string): string[] {
  if (!workingDays) return DEFAULT_WORKING_DAYS;

  const trimmed = workingDays.trim();

  // Handle JSON array format from DB, e.g. "[1,2,3,4,5]" (day numbers)
  if (trimmed.startsWith('[')) {
    try {
      const parsed: (number | string)[] = JSON.parse(trimmed);
      return parsed
        .map(d => DAY_NUMBER_MAP[String(d)] ?? DAY_NAME_MAP[String(d)] ?? String(d))
        .filter(Boolean);
    } catch {
      // Fall through to comma-separated parsing
    }
  }

  // Comma-separated day names or numbers (e.g. "Mon,Tue,Wed" or "1,2,3,4,5")
  return trimmed.split(',').map(d => {
    const t = d.trim();
    return DAY_NUMBER_MAP[t] ?? DAY_NAME_MAP[t] ?? t;
  }).filter(Boolean);
}

/**
 * Parse per-day custom hours from the DB.
 * The DB stores this as a JSON object keyed by day number, e.g. '{"1":7.5,"2":7.5,...}'.
 * Returns a map keyed by full day name, e.g. { Monday: 7.5, Tuesday: 7.5 }.
 */
function parseDailyHours(hoursPerDay?: string): Record<string, number> | undefined {
  if (!hoursPerDay) return undefined;

  const trimmed = hoursPerDay.trim();
  if (!trimmed.startsWith('{')) return undefined;

  try {
    const parsed: Record<string, number> = JSON.parse(trimmed);
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const dayName = DAY_NUMBER_MAP[key] ?? DAY_NAME_MAP[key];
      if (dayName && typeof value === 'number') {
        result[dayName] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
}

function buildWorkingPattern(emp: ApiEmployee): WorkingPattern {
  const totalHoursPerWeek = emp.hoursPerWeek ?? DEFAULT_HOURS_PER_WEEK;
  const workingDays = parseWorkingDays(emp.workingDays);
  const dailyHours = parseDailyHours(emp.hoursPerDay);
  const hoursPerDay = workingDays.length > 0
    ? totalHoursPerWeek / workingDays.length
    : DEFAULT_HOURS_PER_DAY;

  return {
    name: emp.workingPatternName || 'Default',
    fte: totalHoursPerWeek / DEFAULT_HOURS_PER_WEEK,
    hoursPerDay,
    totalHoursPerWeek,
    workingDays,
    dailyHours,
  };
}

function mapApiEmployee(
  emp: ApiEmployee,
  allocations: ResourceAllocation[]
): DirectoryResource {
  const empAllocations = allocations.filter(a => a.employeeId === emp.id);
  return {
    id: String(emp.id),
    name: emp.name,
    email: emp.email,
    phone: '',
    role: emp.role,
    department: emp.department,
    location: '',
    image: emp.photo || '',
    type: 'Person',
    status: emp.isAvailable ? 'Active' : 'Inactive',
    grade: '',
    resourceType: '',
    skills: (emp.skills || []).map(s => ({ name: s, proficiency: 0, category: '' })),
    workingPattern: buildWorkingPattern(emp),
    projectAllocations: empAllocations.map(a => ({
      projectId: a.projectId,
      projectName: a.projectName || `Project ${a.projectId}`,
      projectCode: '',
      allocation: a.allocationPercent || 0,
      startDate: a.startDate,
      endDate: a.endDate,
      role: a.role,
    })),
    rates: {
      billRate: { day: emp.billRate * 8, hour: emp.billRate },
      costRate: { day: emp.costRate * 8, hour: emp.costRate },
    },
  };
}

// Star rating component
const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

// Resource detail sheet component
const ResourceDetailSheet: React.FC<{
  employee: DirectoryResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigate: (path: string) => void;
}> = ({ employee, open, onOpenChange, navigate }) => {
  if (!employee) return null;

  const statusColors = {
    Active: 'bg-green-100 text-green-800 border-green-200',
    'On Leave': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Inactive: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col">
        {/* Header Section */}
        <div className="p-6 border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 rounded-lg">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback className="text-xl">
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{employee.name}</h2>
                  <p className="text-gray-600 mt-1">{employee.role}</p>
                </div>
                <Badge
                  className={`${statusColors[employee.status]}`}
                  variant="outline"
                >
                  {employee.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Section */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Profile</h3>
          <div className="grid grid-cols-2 gap-6">
            {/* Contact Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-500 uppercase">Contact</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-teal-600">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                {employee.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{employee.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Work Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-500 uppercase">Work</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Type:</span>
                  <Badge variant="secondary" className="ml-auto">
                    {employee.type}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Role:</span>
                  <span className="ml-auto font-medium">{employee.role}</span>
                </div>
                {employee.grade && (
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Grade:</span>
                    <Badge variant="outline" className="ml-auto">
                      {employee.grade}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Department:</span>
                  <span className="ml-auto font-medium">{employee.department}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="skills" className="h-full flex flex-col">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6">
              <TabsTrigger value="skills" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Skills
              </TabsTrigger>
              <TabsTrigger value="rates" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Rates
              </TabsTrigger>
              <TabsTrigger value="working-pattern" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Working Pattern
              </TabsTrigger>
              <TabsTrigger value="projects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Projects
              </TabsTrigger>
            </TabsList>

            {/* Skills Tab */}
            <TabsContent value="skills" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Skills & Proficiency</h3>
              </div>
              <div className="space-y-3">
                {employee.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium">{skill.name}</p>
                        {skill.category && (
                          <Badge variant="secondary" className="text-xs">
                            {skill.category}
                          </Badge>
                        )}
                      </div>
                      {skill.proficiency > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <StarRating rating={skill.proficiency} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Rates Tab */}
            <TabsContent value="rates" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Bill Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-gray-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        £{employee.rates.billRate.day.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-gray-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        £{employee.rates.billRate.hour.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-semibold mb-4">Cost Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-gray-700">
                        £{employee.rates.costRate.day.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-gray-700">
                        £{employee.rates.costRate.hour.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Working Pattern Tab */}
            <TabsContent value="working-pattern" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="space-y-6">
                {/* Header with Pattern Name and Total Hours */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Working Pattern</h3>
                    <Badge className="bg-teal-600 text-white hover:bg-teal-600">{employee.workingPattern.name}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1">Total Hours</p>
                    <p className="text-4xl font-bold text-gray-900">{employee.workingPattern.totalHoursPerWeek} h</p>
                  </div>
                </div>

                {/* Working Days & Hours Table */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Working Days & Hours</h4>
                  <div className="border rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="grid grid-cols-3 bg-gray-50 border-b">
                      <div className="px-4 py-3 text-sm font-medium text-gray-700">Day</div>
                      <div className="px-4 py-3 text-sm font-medium text-gray-700 text-center">Status</div>
                      <div className="px-4 py-3 text-sm font-medium text-gray-700 text-right">Hours</div>
                    </div>
                    {/* Table Rows */}
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                      const isWorking = employee.workingPattern.workingDays.includes(day);
                      const hours = isWorking
                        ? (employee.workingPattern.dailyHours?.[day] ?? employee.workingPattern.hoursPerDay)
                        : 0;
                      return (
                        <div
                          key={day}
                          className={`grid grid-cols-3 border-b last:border-b-0 ${
                            isWorking ? 'bg-teal-50/30' : 'bg-white'
                          }`}
                        >
                          <div className={`px-4 py-3 text-sm ${isWorking ? 'text-gray-900' : 'text-gray-400'}`}>
                            {day}
                          </div>
                          <div className="px-4 py-3 flex justify-center">
                            <div
                              className={`w-10 h-6 rounded-full transition-colors ${
                                isWorking ? 'bg-teal-500' : 'bg-gray-300'
                              } relative`}
                            >
                              <div
                                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                  isWorking ? 'right-0.5' : 'left-0.5'
                                }`}
                              />
                            </div>
                          </div>
                          <div className={`px-4 py-3 text-sm text-right ${isWorking ? 'text-gray-900' : 'text-gray-400'}`}>
                            {hours.toFixed(2)} h
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary Section */}
                <div className="bg-teal-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Working Days</p>
                      <p className="text-2xl font-bold text-gray-900">{employee.workingPattern.workingDays.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Hours</p>
                      <p className="text-2xl font-bold text-gray-900">{employee.workingPattern.totalHoursPerWeek} h</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Avg per Day</p>
                      <p className="text-2xl font-bold text-gray-900">{employee.workingPattern.hoursPerDay.toFixed(2)} h</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Projects Tab */}
            <TabsContent value="projects" className="flex-1 overflow-y-auto p-6 mt-0">
              <h3 className="text-lg font-semibold mb-4">Project Allocations</h3>
              {employee.projectAllocations.length > 0 ? (
                <div className="space-y-3">
                  {employee.projectAllocations.map((project, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <button
                              onClick={() => navigate(`/projects/${project.projectId}`)}
                              className="text-left hover:underline focus:outline-none focus:underline"
                            >
                              <p className="font-semibold text-lg text-teal-600 hover:text-teal-700">
                                {project.projectName}
                              </p>
                              <p className="text-sm text-teal-500 hover:text-teal-600">{project.projectCode}</p>
                            </button>
                          </div>
                          <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                            {project.allocation}% Allocated
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Role:</span>
                            <span className="font-medium">{project.role}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>
                              {new Date(project.startDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}{' '}
                              -{' '}
                              {new Date(project.endDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No current project allocations</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Default column definitions for Resource Directory
const DEFAULT_DIRECTORY_COLUMNS: ColumnDefinition[] = [
  {
    id: 'name',
    name: 'Name',
    description: 'Employee name with avatar',
    category: 'Status',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'role',
    name: 'Role',
    description: 'Job title or role',
    category: 'Status',
    visible: true,
    order: 1,
  },
  {
    id: 'department',
    name: 'Department',
    description: 'Department or team',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Email address',
    category: 'Client',
    visible: true,
    order: 3,
  },
  {
    id: 'phone',
    name: 'Phone',
    description: 'Phone number',
    category: 'Client',
    visible: true,
    order: 4,
  },
  {
    id: 'location',
    name: 'Location',
    description: 'Office location',
    category: 'Status',
    visible: true,
    order: 5,
  },
  {
    id: 'type',
    name: 'Type',
    description: 'Person or Asset',
    category: 'Status',
    visible: true,
    order: 6,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Employment status',
    category: 'Status',
    visible: false,
    order: 7,
  },
  {
    id: 'grade',
    name: 'Grade',
    description: 'Seniority level',
    category: 'Resource',
    visible: false,
    order: 8,
  },
  {
    id: 'resource-type',
    name: 'Resource Type',
    description: 'Technical, Creative, etc.',
    category: 'Resource',
    visible: false,
    order: 9,
  },
  {
    id: 'fte',
    name: 'FTE',
    description: 'Full-time equivalent',
    category: 'Resource',
    visible: false,
    order: 10,
    align: 'right',
  },
  {
    id: 'hours-per-week',
    name: 'Hours/Week',
    description: 'Total hours per week',
    category: 'Resource',
    visible: false,
    order: 11,
    align: 'right',
  },
  {
    id: 'bill-rate-day',
    name: 'Bill Rate (Day)',
    description: 'Daily billing rate',
    category: 'Financial',
    visible: false,
    order: 12,
    align: 'right',
  },
  {
    id: 'bill-rate-hour',
    name: 'Bill Rate (Hour)',
    description: 'Hourly billing rate',
    category: 'Financial',
    visible: false,
    order: 13,
    align: 'right',
  },
  {
    id: 'cost-rate-day',
    name: 'Cost Rate (Day)',
    description: 'Daily cost rate',
    category: 'Financial',
    visible: false,
    order: 14,
    align: 'right',
  },
  {
    id: 'cost-rate-hour',
    name: 'Cost Rate (Hour)',
    description: 'Hourly cost rate',
    category: 'Financial',
    visible: false,
    order: 15,
    align: 'right',
  },
  {
    id: 'total-allocation',
    name: 'Total Allocation',
    description: 'Total project allocation %',
    category: 'Performance',
    visible: false,
    order: 16,
    align: 'right',
  },
  {
    id: 'project-count',
    name: 'Active Projects',
    description: 'Number of active projects',
    category: 'Performance',
    visible: false,
    order: 17,
    align: 'right',
  },
];

export function DirectoryPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedEmployee, setSelectedEmployee] = useState<DirectoryResource | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [employees, setEmployees] = useState<DirectoryResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apiEmployees, allocations] = await Promise.all([
        fetchEmployees(),
        fetchResourceAllocations().catch(() => [] as ResourceAllocation[]),
      ]);
      const mapped = (apiEmployees as ApiEmployee[]).map(emp =>
        mapApiEmployee(emp, allocations as ResourceAllocation[])
      );
      setEmployees(mapped);
    } catch (err) {
      console.error('Failed to load directory data:', err);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Use column preferences hook for persistence
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'directory-column-preferences',
    defaultColumns: DEFAULT_DIRECTORY_COLUMNS,
  });

  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });

  // Get unique departments and roles for filters
  const departments = useMemo(
    () => ['all', ...Array.from(new Set(employees.map((e) => e.department).filter(Boolean)))],
    [employees]
  );
  const roles = useMemo(
    () => ['all', ...Array.from(new Set(employees.map((e) => e.role).filter(Boolean)))],
    [employees]
  );

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) => {
        const matchesSearch =
          employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesDepartment =
          departmentFilter === 'all' || employee.department === departmentFilter;
        const matchesRole = roleFilter === 'all' || employee.role === roleFilter;
        return matchesSearch && matchesDepartment && matchesRole;
      })
      .sort((a, b) => {
        if (sortBy === 'name') {
          return a.name.localeCompare(b.name);
        }
        return 0;
      });
  }, [employees, searchQuery, departmentFilter, roleFilter, sortBy]);

  // Reset to first page when filters change
  useEffect(() => {
    resetPagination();
  }, [searchQuery, departmentFilter, roleFilter]);

  // Paginate the filtered results
  const paginatedResult = useMemo(() => {
    return paginateArray(filteredEmployees, currentPage, pageSize);
  }, [filteredEmployees, currentPage, pageSize]);

  const displayedEmployees = paginatedResult.data;
  const pagination = paginatedResult.pagination;

  const handleEmployeeClick = (employee: DirectoryResource) => {
    setSelectedEmployee(employee);
    setSheetOpen(true);
  };

  // Get visible columns sorted by order
  const visibleColumns = useMemo(() => {
    return columns
      .filter((col) => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  // Helper function to render table cell content based on column ID
  const renderTableCell = (employee: DirectoryResource, columnId: string) => {
    switch (columnId) {
      case 'name':
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-md">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback>
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{employee.name}</span>
          </div>
        );
      case 'role':
        return employee.role;
      case 'department':
        return <Badge variant="outline">{employee.department}</Badge>;
      case 'email':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{employee.email}</span>
          </div>
        );
      case 'phone':
        return employee.phone ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>{employee.phone}</span>
          </div>
        ) : <span className="text-muted-foreground">-</span>;
      case 'location':
        return employee.location ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{employee.location}</span>
          </div>
        ) : <span className="text-muted-foreground">-</span>;
      case 'type':
        return <Badge variant="secondary">{employee.type}</Badge>;
      case 'status':
        return <Badge variant={employee.status === 'Active' ? 'default' : 'secondary'}>{employee.status}</Badge>;
      case 'grade':
        return employee.grade || <span className="text-muted-foreground">-</span>;
      case 'resource-type':
        return employee.resourceType || <span className="text-muted-foreground">-</span>;
      case 'fte':
        return employee.workingPattern.fte.toFixed(2);
      case 'hours-per-week':
        return employee.workingPattern.totalHoursPerWeek;
      case 'bill-rate-day':
        return `${employee.rates.billRate.day.toLocaleString()}`;
      case 'bill-rate-hour':
        return `${employee.rates.billRate.hour.toLocaleString()}`;
      case 'cost-rate-day':
        return `${employee.rates.costRate.day.toLocaleString()}`;
      case 'cost-rate-hour':
        return `${employee.rates.costRate.hour.toLocaleString()}`;
      case 'total-allocation':
        const totalAllocation = employee.projectAllocations.reduce((sum, alloc) => sum + alloc.allocation, 0);
        return `${totalAllocation}%`;
      case 'project-count':
        return employee.projectAllocations.length;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Resource Directory</h1>
        <p className="text-gray-600 mt-1">Browse and manage your organization's resources</p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by name, role, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[180px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept === 'all' ? 'All Departments' : dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <Briefcase className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role} value={role}>
                  {role === 'all' ? 'All Roles' : role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex gap-1 border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {viewMode === 'list' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setColumnConfigOpen(true)}
              className="gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Columns
            </Button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{pagination.startIndex + 1}</span> to{' '}
        <span className="font-semibold">{pagination.endIndex}</span> of{' '}
        <span className="font-semibold">{filteredEmployees.length}</span> employees
        {filteredEmployees.length !== employees.length && (
          <span className="text-gray-500"> (filtered from {employees.length} total)</span>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading employees...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-red-500 mb-2">{error}</p>
          <Button variant="outline" onClick={loadEmployees}>Retry</Button>
        </div>
      )}

      {/* Employee Grid/List */}
      {!loading && !error && displayedEmployees.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {displayedEmployees.map((employee) => (
              <Card
                key={employee.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleEmployeeClick(employee)}
              >
                <div className="relative overflow-hidden h-48">
                  {employee.image ? (
                    <img
                      src={employee.image}
                      alt={employee.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Hide the image and show fallback by replacing the parent content
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          e.currentTarget.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300';
                          const initials = document.createElement('span');
                          initials.className = 'text-5xl font-semibold text-white';
                          initials.textContent = employee.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('');
                          fallback.appendChild(initials);
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                      <span className="text-5xl font-semibold text-white">
                        {employee.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </span>
                    </div>
                  )}
                  <Badge
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm"
                    variant="secondary"
                  >
                    {employee.type}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{employee.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{employee.role}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{employee.email}</span>
                    </div>
                    {employee.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="h-3 w-3" />
                        <span>{employee.phone}</span>
                      </div>
                    )}
                    {employee.location && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span>{employee.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <Badge variant="outline" className="text-xs">
                      {employee.department}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={column.align === 'right' ? 'text-right' : ''}
                    >
                      {column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEmployeeClick(employee)}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={column.align === 'right' ? 'text-right' : ''}
                      >
                        {renderTableCell(employee, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : !loading && !error ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No employees found matching your criteria.</p>
          <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filters.</p>
        </div>
      ) : null}

      {/* Pagination Controls */}
      {filteredEmployees.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          showPageSizeSelector={true}
        />
      )}

      {/* Resource Detail Sheet */}
      <ResourceDetailSheet
        employee={selectedEmployee}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        navigate={navigate}
      />

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={updateColumns}
        defaultColumns={DEFAULT_DIRECTORY_COLUMNS}
        pageKey="directory"
      />
    </div>
  );
}
